import { User } from '@/types';

const ROLE_FALLBACK_PERMISSIONS: Record<string, string[]> = {
  SuperAdmin: ['dashboard.view', 'technical.view', 'sales.view', 'accounts.view', 'hr.view', 'library.view', 'vehicles.view', 'vehicles.manage', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'vehicles.reset', 'users.view', 'users.manage', 'users.view_department', 'users.manage_department', 'reports.view', 'target.manage', 'newsletter.view', 'newsletter.manage', 'requisitions.view', 'profile.view'],
  CEO: ['dashboard.view', 'technical.view', 'sales.view', 'accounts.view', 'hr.view', 'library.view', 'vehicles.view', 'vehicles.manage', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'vehicles.reset', 'users.view', 'users.manage', 'users.view_department', 'users.manage_department', 'reports.view', 'target.manage', 'newsletter.view', 'newsletter.manage', 'requisitions.view', 'profile.view'],
  'Executive Assistant': ['dashboard.view', 'sales.view', 'accounts.view', 'hr.view', 'library.view', 'vehicles.view', 'vehicles.request', 'users.view', 'reports.view', 'target.manage', 'newsletter.view', 'newsletter.manage', 'profile.view'],
  'Technical HOD': ['dashboard.view', 'technical.view', 'hr.view', 'users.view_department', 'users.manage_department', 'requisitions.view', 'profile.view'],
  'Field Operations Head': ['dashboard.view', 'technical.view', 'hr.view', 'users.view_department', 'users.manage_department', 'requisitions.view', 'profile.view'],
  'Support Head': ['dashboard.view', 'technical.view', 'hr.view', 'users.view_department', 'users.manage_department', 'profile.view'],
  'Field Technician': ['dashboard.view', 'technical.view', 'hr.view', 'requisitions.view', 'profile.view'],
  'Customer Support': ['dashboard.view', 'technical.view', 'hr.view', 'profile.view'],
  'Sales HOD': ['dashboard.view', 'sales.view', 'hr.view', 'users.view_department', 'users.manage_department', 'profile.view'],
  'Sales Person': ['dashboard.view', 'sales.view', 'hr.view', 'profile.view'],
  'Accounts HOD': ['dashboard.view', 'accounts.view', 'hr.view', 'users.view_department', 'users.manage_department', 'requisitions.view', 'profile.view'],
  Accounts: ['dashboard.view', 'accounts.view', 'hr.view', 'profile.view'],
  'Accounts Clerk': ['dashboard.view', 'accounts.view', 'hr.view', 'requisitions.view', 'profile.view'],
  'Billing Clerk': ['dashboard.view', 'accounts.view', 'profile.view'],
  'Activations Accounts Clerk': ['dashboard.view', 'accounts.view', 'profile.view'],
  'Stock Clerk': ['dashboard.view', 'accounts.view', 'hr.view', 'requisitions.view', 'profile.view'],
  HR: ['dashboard.view', 'hr.view', 'users.view_department', 'users.manage_department', 'profile.view'],
  Librarian: ['dashboard.view', 'library.view', 'hr.view', 'profile.view'],
  Reader: ['dashboard.view', 'library.view', 'hr.view', 'profile.view'],
  'Vehicle Admin': ['dashboard.view', 'vehicles.view', 'vehicles.manage', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'vehicles.reset', 'profile.view'],
  'Sales Vehicle Admin': ['dashboard.view', 'vehicles.view', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'profile.view'],
  'Technical Vehicle Admin': ['dashboard.view', 'vehicles.view', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'profile.view'],
  'Accounts Vehicle Admin': ['dashboard.view', 'vehicles.view', 'vehicles.request', 'vehicles.approve', 'vehicles.inspect', 'profile.view'],
  Driver: ['dashboard.view', 'vehicles.view', 'vehicles.request', 'profile.view'],
  'Office Admin': ['dashboard.view', 'requisitions.view', 'profile.view'],
};

const ROUTE_PERMISSION_RULES: Array<[string, string]> = [
  ['/newsletter/create', 'newsletter.manage'],
  ['/target', 'target.manage'],
  ['/reports', 'reports.view'],
  ['/requisitions', 'requisitions.view'],
  ['/profile', 'profile.view'],
  ['/vehicles', 'vehicles.view'],
  ['/library', 'library.view'],
  ['/hr', 'hr.view'],
  ['/accounts', 'accounts.view'],
  ['/sales', 'sales.view'],
  ['/technical', 'technical.view'],
  ['/dashboard', 'dashboard.view'],
  ['/newsletter', 'newsletter.view'],
];

export const getEffectivePermissions = (user: User | null | undefined): string[] => {
  if (!user) return [];
  if (Array.isArray(user.effective_permissions) && user.effective_permissions.length > 0) {
    return user.effective_permissions;
  }

  const roleNames = Array.isArray(user.roles) ? user.roles.map(role => role.name) : [];
  const permissions = new Set<string>(['profile.view']);
  roleNames.forEach(role => {
    (ROLE_FALLBACK_PERMISSIONS[role] || []).forEach(permission => permissions.add(permission));
  });
  return Array.from(permissions);
};

export const hasPermission = (user: User | null | undefined, permission?: string): boolean => {
  if (!permission) return true;
  return getEffectivePermissions(user).includes(permission);
};

export const getRequiredPermissionForPath = (path: string): string | undefined => {
  if (path === '/users/create' || path.endsWith('/edit')) {
    return 'users.manage';
  }
  if (path.startsWith('/users/')) {
    return 'users.view';
  }
  return ROUTE_PERMISSION_RULES.find(([prefix]) => path.startsWith(prefix))?.[1];
};
